// When a thread shows the time, and when it stays out of the way. Lifted from
// the Andon app's `lib/thread.ts`.
//
// THE PATTERN, AND WHY THIS ONE
//
// Messaging apps converged on the same answer years ago and it is worth not
// re-deriving badly: group messages sent close together by the same person,
// give the GROUP one timestamp rather than each message its own, and put the
// per-message time behind a tap for anybody who needs it.
//
// It suits a factory thread better than it suits a social one. A run of four
// messages from the responder who is elbow-deep in the machine is one
// utterance, and four clock times down the side of it is four things to read
// that all say the same thing. What somebody actually wants is "when did this
// start" and "how long ago was the last thing anybody said" — which is the
// first and last of a run, not every line in it.
//
// So: a quiet time on the LAST bubble of each run, always, because that is the
// one the eye lands on and the one that answers "is this current". And a tap
// anywhere in the thread reveals the exact time on every bubble, for the times
// somebody is reconstructing what happened.

/** Messages closer together than this, from one author, read as one run. */
export const RUN_GAP_MS = 5 * 60_000;

export interface Timed {
  /** Epoch ms. */
  ts: number;
  /** Null for anything not attributable to a person — an event. */
  author: string | null;
}

/**
 * Whether this entry ends a run — the one that carries the group's time.
 *
 * The LAST of a run rather than the first, deliberately. A thread is read from
 * the bottom, and the question being asked of a timestamp is almost always
 * "how old is this", not "when did it begin".
 *
 * Anything with no author (an event divider) breaks a run on both sides:
 * something happened in between, so the messages either side of it are not
 * one utterance however close together they were sent.
 */
export function endsRun(entries: Timed[], i: number): boolean {
  const here = entries[i];
  if (!here || here.author == null) return false;
  const next = entries[i + 1];
  if (!next) return true;
  if (next.author !== here.author) return true;
  return next.ts - here.ts > RUN_GAP_MS;
}

/**
 * Whether this entry OPENS a run — the one that carries the name and avatar.
 *
 * The other half of grouping, and the half that removes the most clutter. Four
 * messages from Regan in one minute repeated her avatar and her name four
 * times; the second, third and fourth said nothing the first had not, and on a
 * 375px screen they cost a third of the width each time.
 *
 * Mirror of `endsRun`: same author, same window, and anything without an
 * author breaks it on both sides.
 */
export function startsRun(entries: Timed[], i: number): boolean {
  const here = entries[i];
  if (!here || here.author == null) return false;
  const previous = entries[i - 1];
  if (!previous) return true;
  if (previous.author !== here.author) return true;
  return here.ts - previous.ts > RUN_GAP_MS;
}

/** `14:36`. Twenty-four hour, because a factory runs on a roster, not on am/pm. */
export function clockTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** The two phrases `ago` needs, in the reader's language. */
export interface AgoWords {
  justNow: string;
  /** Follows the number: "3 min ago". */
  minAgo: string;
}

/**
 * How long ago, in the words somebody would use out loud.
 *
 * Cuts over to a clock time after an hour. "97 minutes ago" is arithmetic
 * nobody performs; "14:36" is a fact about the shift, and by then that is the
 * useful frame — you are asking which part of the morning this was, not
 * counting minutes.
 *
 * Falls back to a clock time for anything in the future too. A device whose
 * clock is ahead of the server's should show something odd rather than "in
 * -3 minutes", and this app already takes every deadline from the server for
 * exactly that reason.
 */
export function ago(ts: number, now: number, words: AgoWords): string {
  const ms = now - ts;
  if (ms < 0) return clockTime(ts);
  if (ms < 60_000) return words.justNow;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} ${words.minAgo}`;
  return clockTime(ts);
}

/**
 * The date a thread crossed into, or null while it is all one day.
 *
 * An andon that outlives a shift is rare and is exactly when somebody is
 * reading the thread to reconstruct what happened — so the day has to appear,
 * once, where it changes. Without it "08:15" three screens up is unreadable:
 * it could be this morning or the one before.
 */
export function dayLabel(ts: number, previous: number | null): string | null {
  const d = new Date(ts);
  if (previous != null) {
    const p = new Date(previous);
    if (p.getFullYear() === d.getFullYear() && p.getMonth() === d.getMonth() && p.getDate() === d.getDate()) {
      return null;
    }
  }
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * An Odoo datetime as epoch ms.
 *
 * They arrive as `2026-08-25 04:36:00`: UTC, second-granular, and with no zone
 * on the end. Handing that to `Date.parse` unmodified is the bug where every
 * timestamp in the thread shifts by the reader's offset — Safari and Chrome do
 * not even agree on which way. So the shape is made explicit before parsing.
 */
export function toEpoch(at: string): number {
  const parsed = Date.parse(`${at.replace(' ', 'T')}Z`);
  return Number.isNaN(parsed) ? 0 : parsed;
}
